import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    Assessment,
    TrendingUp,
    TrendingDown,
    CheckCircle,
    Warning,
    Error,
    Person,
    OndemandVideo,
    PhotoLibrary,
    Build,
    Schedule
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const SafetyAnalytics = ({ analytics }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(!analytics);

    useEffect(() => {
        if (analytics) {
            setLoading(false);
        }
    }, [analytics]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!analytics) {
        return (
            <Alert severity="info">
                Analytics data is not available at this time.
            </Alert>
        );
    }

    const getCompletionRate = () => {
        const total = analytics.completionStats?.reduce((sum, stat) => sum + stat.count, 0) || 0;
        const completed = analytics.completionStats?.find(stat => stat._id === 'completed')?.count || 0;
        return total > 0 ? Math.round((completed / total) * 100) : 0;
    };

    const getContentTypeIcon = (type) => {
        switch (type) {
            case 'general_safety':
                return <CheckCircle color="primary" />;
            case 'moffett_training':
                return <Build color="warning" />;
            case 'truss_trailer':
                return <Assessment color="info" />;
            case 'load_securing':
                return <PhotoLibrary color="success" />;
            case 'equipment_operation':
                return <Build color="secondary" />;
            default:
                return <OndemandVideo color="primary" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'warning';
            case 'overdue':
                return 'error';
            default:
                return 'default';
        }
    };

    const completionRate = getCompletionRate();

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Assessment color="primary" fontSize="large" />
                <Typography variant="h5">
                    Safety Analytics Dashboard
                </Typography>
            </Stack>

            <Grid container spacing={3}>
                {/* Overview Stats */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Training Completion Overview
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={6} md={3}>
                                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                    <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                    <Typography variant="h4" color="success.main">
                                        {analytics.completionStats?.find(s => s._id === 'completed')?.count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Completed
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                    <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                    <Typography variant="h4" color="warning.main">
                                        {analytics.completionStats?.find(s => s._id === 'in_progress')?.count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        In Progress
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                    <Error sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                                    <Typography variant="h4" color="error.main">
                                        {analytics.overdueCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Overdue
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                    <Warning sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                    <Typography variant="h4" color="info.main">
                                        {analytics.dueForReview || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Due for Review
                                    </Typography>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Overall Progress Bar */}
                        <Box sx={{ mt: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1">Overall Completion Rate</Typography>
                                <Typography variant="h6" color="primary">
                                    {completionRate}%
                                </Typography>
                            </Stack>
                            <LinearProgress 
                                variant="determinate" 
                                value={completionRate}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Content Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Content by Category
                            </Typography>
                            {analytics.contentStats && analytics.contentStats.length > 0 ? (
                                <List>
                                    {analytics.contentStats.map((stat, index) => (
                                        <React.Fragment key={stat._id}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {getContentTypeIcon(stat._id)}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={stat._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Other'}
                                                    secondary={`${stat.count} items`}
                                                />
                                                <Chip 
                                                    label={stat.count} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                />
                                            </ListItem>
                                            {index < analytics.contentStats.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">No content statistics available</Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Completion Status Breakdown */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Training Status Breakdown
                            </Typography>
                            {analytics.completionStats && analytics.completionStats.length > 0 ? (
                                <List>
                                    {analytics.completionStats.map((stat, index) => (
                                        <React.Fragment key={stat._id}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {stat._id === 'completed' && <CheckCircle color="success" />}
                                                    {stat._id === 'in_progress' && <Schedule color="warning" />}
                                                    {stat._id === 'not_started' && <Error color="error" />}
                                                    {stat._id === 'overdue' && <Warning color="error" />}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={stat._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    secondary={`${stat.count} users`}
                                                />
                                                <Chip 
                                                    label={stat.count} 
                                                    size="small" 
                                                    color={getStatusColor(stat._id)}
                                                    variant="outlined"
                                                />
                                            </ListItem>
                                            {index < analytics.completionStats.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">No completion statistics available</Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Activity */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Recent Training Activity
                            </Typography>
                            {analytics.recentActivity && analytics.recentActivity.length > 0 ? (
                                <List>
                                    {analytics.recentActivity.map((activity, index) => (
                                        <React.Fragment key={activity._id}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Person color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={activity.user?.name || 'Unknown User'}
                                                    secondary={
                                                        <Stack spacing={1}>
                                                            <Typography variant="body2">
                                                                {activity.safetyContent?.title || 'Unknown Content'}
                                                            </Typography>
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip 
                                                                    label={activity.safetyContent?.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Other'} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                />
                                                                <Chip 
                                                                    label={activity.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                                                                    size="small" 
                                                                    color={getStatusColor(activity.status)}
                                                                    variant="outlined"
                                                                />
                                                            </Stack>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(activity.updatedAt).toLocaleString()}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                />
                                            </ListItem>
                                            {index < analytics.recentActivity.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">No recent activity found</Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Action Items */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Action Items
                        </Typography>
                        <Grid container spacing={2}>
                            {analytics.overdueCount > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Alert severity="error">
                                        <Typography variant="subtitle2">
                                            {analytics.overdueCount} Overdue Training Items
                                        </Typography>
                                        <Typography variant="body2">
                                            Immediate action required to ensure compliance
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}
                            
                            {analytics.dueForReview > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Alert severity="warning">
                                        <Typography variant="subtitle2">
                                            {analytics.dueForReview} Items Due for Review
                                        </Typography>
                                        <Typography variant="body2">
                                            Review and recertification needed
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}

                            {completionRate < 70 && (
                                <Grid item xs={12} md={6}>
                                    <Alert severity="warning">
                                        <Typography variant="subtitle2">
                                            Low Completion Rate ({completionRate}%)
                                        </Typography>
                                        <Typography variant="body2">
                                            Consider additional training initiatives
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}

                            {analytics.overdueCount === 0 && analytics.dueForReview === 0 && completionRate >= 70 && (
                                <Grid item xs={12}>
                                    <Alert severity="success">
                                        <Typography variant="subtitle2">
                                            Safety Training On Track
                                        </Typography>
                                        <Typography variant="body2">
                                            All safety training requirements are being met effectively
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SafetyAnalytics; 